/**
 * How a document body gets to the server and back when it is too big to just go.
 *
 * THE CEILING IS THE LAMBDA'S. API Gateway allows 10MB; a synchronous Lambda invoke allows 6MB,
 * counted separately on the request and on the response. A document body travels as JSON, so 6MB is
 * the limit in each direction and the `.docx` import reaches it at about a 4.4MB file because that
 * one is base64. See cres_t11/utils/body_transport.py, which is the other half of this.
 *
 * TWO MECHANISMS, IN THIS ORDER:
 *
 *   gzip  This JSON compresses about ten to one -- every node names its type and its marks, so it is
 *         deeply repetitive. One round trip and no S3. It covers everything we can foresee.
 *   S3    Beyond that the bytes go round the lambda: ask for a slot, PUT the gzipped body to a
 *         presigned URL, and send the key instead of the document.
 *
 * NEGOTIATED, AND DEGRADING QUIETLY. `Accept` tells the server what this browser understands and the
 * server answers in kind; a browser with no CompressionStream asks for nothing and gets a plain
 * `Body`, exactly as before. The frontend and the lambda are not deployed in the same instant, so a
 * response an older tab cannot read is a document that will not open.
 */

/**
 * Compression in the browser with no library.
 *
 * NOT ASSUMED PRESENT. CompressionStream is in every current browser and in none of the old ones,
 * and a document that will not open is a worse outcome than a document that saves uncompressed.
 * Feature-detected once; everything downstream reads the answer.
 */
const GZIP_AVAILABLE =
    typeof (globalThis as any).CompressionStream === "function" &&
    typeof (globalThis as any).DecompressionStream === "function";

/** Below this many bytes of JSON, compressing costs more than it saves. Mirrors GZIP_FLOOR. */
const GZIP_FLOOR = 64 * 1024;

/** Above this many bytes of encoded payload, stop sending it through the lambda. Mirrors S3_CEILING. */
const S3_CEILING = 3 * 1024 * 1024;

/** What this browser can read, for the `Accept` parameter. Empty when it can only read `Body`. */
export function accept(): string {
    return GZIP_AVAILABLE ? "gzip,s3" : "";
}

async function streamThrough(bytes: Uint8Array, transform: any): Promise<Uint8Array> {
    const stream = new Blob([bytes as any]).stream().pipeThrough(transform);
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

function toBase64(bytes: Uint8Array): string {
    // CHUNKED, because String.fromCharCode.apply on a megabyte-long array overflows the call stack
    // -- which shows up as a save that throws only for the documents big enough to need this path.
    let out = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        out += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as any);
    }
    return btoa(out);
}

function fromBase64(text: string): Uint8Array {
    const binary = atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

export async function gzipBytes(body: any): Promise<Uint8Array> {
    const raw = new TextEncoder().encode(JSON.stringify(body));
    return streamThrough(raw, new (globalThis as any).CompressionStream("gzip"));
}

async function gunzip(bytes: Uint8Array): Promise<any> {
    const raw = await streamThrough(bytes, new (globalThis as any).DecompressionStream("gzip"));
    return JSON.parse(new TextDecoder().decode(raw));
}

/**
 * The document out of whichever representation the server sent.
 *
 * THREE SHAPES, ONE OF THEM AUTHORITATIVE. The server replaces `Body` rather than adding beside it,
 * so there is never a question which one to believe.
 */
export async function readBody(payload: any): Promise<any> {
    if (!payload) return payload;

    if (payload.BodyGzip) {
        return gunzip(fromBase64(payload.BodyGzip));
    }

    if (payload.BodyUrl) {
        // STRAIGHT TO S3, with no session header: a presigned URL carries its own authorisation and
        // sending ours would be rejected by the signature check.
        const response = await fetch(payload.BodyUrl);
        if (!response.ok) throw new Error(`The document body could not be fetched (${response.status}).`);
        const bytes = new Uint8Array(await response.arrayBuffer());
        return payload.BodyEncoding === "gzip" ? gunzip(bytes) : JSON.parse(new TextDecoder().decode(bytes));
    }

    return payload.Body;
}

/** A read result with its body resolved in place, so callers go on reading `.Body` as they did. */
export async function withBody(payload: any): Promise<any> {
    if (!payload || (!payload.BodyGzip && !payload.BodyUrl)) return payload;
    const body = await readBody(payload);
    const {BodyGzip, BodyUrl, BodyEncoding, ...rest} = payload;
    return {...rest, Body: body};
}

/**
 * What a save should put in its payload for this document.
 *
 * `slot` is called only when the body is big enough to need S3 -- asking for an upload slot is a
 * round trip and a presigned PUT, and almost every save wants neither.
 */
export async function writeBody(
    body: any,
    slot: () => Promise<{Url: string; Key: string; ContentType?: string}>,
): Promise<{Body?: any; BodyGzip?: string; BodyKey?: string}> {
    const raw = JSON.stringify(body);

    if (!GZIP_AVAILABLE || raw.length < GZIP_FLOOR) {
        return {Body: body};
    }

    const packed = await gzipBytes(body);
    const encoded = toBase64(packed);

    if (encoded.length <= S3_CEILING) {
        return {BodyGzip: encoded};
    }

    const reserved = await slot();
    const response = await fetch(reserved.Url, {
        method: "PUT",
        headers: {"Content-Type": reserved.ContentType || "application/gzip"},
        body: packed as any,
    });
    if (!response.ok) {
        throw new Error(`The document could not be staged for saving (${response.status}).`);
    }
    return {BodyKey: reserved.Key};
}
